import React, {useState, useEffect} from 'react';
import _get from 'lodash/get';
import {Trans} from 'react-i18next';
import ClickDisabledPopup from './ClickDisabledPopup';
import Category from './Category';
import {processItemList} from './utils';
import skinDetectionCategories from '../../../../utils/config/skinDetectionCategories';

export default function ToggleEnableField({
  itemList = [],
  onChange,
  minEnable = 0,
  preview = false,
  disableItems = [],
  isEnterprise = false,
  isSupportSubOptions = false,
  type = 'SKIN_ANALYSIS', //AESTHETIC_SIMULATION
  sortable = true,
}) {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [statusList, setStatusList] = useState(processItemList(itemList));

  useEffect(() => {
    if (!itemList || itemList.length === 0) return;
    setStatusList(processItemList(itemList));
  }, [itemList]);

  const toggleItemStatus = (
    value,
    action,
    isSubOption = false,
    isAllSubOptionsChanged = false
  ) => {
    const numOfEnabled = statusList.filter(i => !i.disabled).length;

    if (action === 'disable' && numOfEnabled <= minEnable) {
      return;
    }

    const updatedList = statusList.map(item => {
      const hasOptions = _get(item, 'options', []).length > 0;
      if (!hasOptions) {
        return item.value === value
          ? {...item, disabled: action === 'disable'}
          : item;
      }

      if (isAllSubOptionsChanged && item.value === value) {
        const updatedSubOptions = item.options.map(option => ({
          ...option,
          disabled: action === 'disable',
        }));

        return {
          ...item,
          disabled: action === 'disable',
          options: [
            ...updatedSubOptions.filter(option => option.isRequired),
            ...updatedSubOptions.filter(option => !option.isRequired),
          ],
        };
      }

      if (isSubOption) {
        const parentValue = skinDetectionCategories.getParentValueByOptionValue(
          value
        );

        if (!parentValue || parentValue !== item.value) {
          return item;
        }

        const requiredOptionValue = skinDetectionCategories.getRequiredOptionValueByParentValue(
          parentValue
        );

        if (action === 'disable' && value === requiredOptionValue) {
          const updatedSubOptions = item.options.map(option => ({
            ...option,
            disabled: item.options.map(option => {
              if (option.value === value) {
                return {...option, disabled: action === 'disable'};
              }
              return option;
            }),
          }));

          return {
            ...item,
            disabled: action === 'disable',
            options: [
              ...updatedSubOptions.filter(option => option.isRequired),
              ...updatedSubOptions.filter(option => !option.isRequired),
            ],
          };
        }

        const updatedSubOptions = item.options.map(option => {
          if (action !== 'disable' && option.value === requiredOptionValue) {
            return {...option, disabled: false};
          }
          if (option.value === value) {
            return {...option, disabled: action === 'disable'};
          }
          return option;
        });

        return {
          ...item,
          disabled: false,
          options: [
            ...updatedSubOptions.filter(option => option.isRequired),
            ...updatedSubOptions.filter(option => !option.isRequired),
          ],
        };
      }

      return item;
    });

    setStatusList(updatedList);
    onChange(updatedList);
  };

  const onDragItem = order => {
    const disabled = statusList.filter(i => i.disabled);
    const newList = order.map(value => statusList.find(i => i.value === value));
    setStatusList([...newList, ...disabled]);
    onChange([...newList, ...disabled]);
  };

  const onDragSubItem = (newOrder, parentValue) => {
    const updatedItemList = [...statusList];
    const parentItem = updatedItemList.find(item => item.value === parentValue);

    const reorderedOptions = newOrder
      .map(value => parentItem.options.find(option => option.value === value))
      .filter(Boolean);

    const remainingOptions = parentItem.options.filter(
      option => !newOrder.includes(option.value)
    );

    const allReorderedOptions = [...reorderedOptions, ...remainingOptions];

    parentItem.options = [
      ...allReorderedOptions.filter(option => option.isRequired),
      ...allReorderedOptions.filter(option => !option.isRequired),
    ];

    setStatusList(updatedItemList);
    onChange(updatedItemList);
  };

  return (
    <>
      <div className="d-flex justify-content-between kt-margin-t-10">
        <Category
          title={<Trans i18nKey="p.s.c.toggle.enable">Enable</Trans>}
          items={statusList
            .map(item => ({
              ...item,
              options: item.options
                ? item.options.filter(subItem => !subItem.disabled)
                : [],
            }))
            .filter(item => !item.disabled || item.options.length > 0)}
          toggleItemStatus={toggleItemStatus}
          preview={preview}
          sortable={sortable}
          onDragItem={onDragItem}
          onDragSubItem={onDragSubItem}
          isSupportSubOptions={isSupportSubOptions}
          type={'enable'}
        />
        <Category
          title={<Trans i18nKey="p.s.c.toggle.disable">Disable</Trans>}
          items={statusList
            .map(item => ({
              ...item,
              options: item.options
                ? item.options.filter(subItem => subItem.disabled)
                : [],
            }))
            .filter(item => item.disabled || item.options.length > 0)}
          toggleItemStatus={(
            value,
            action,
            isSubOptions = false,
            isAllSubOptionsChanged = false
          ) =>
            disableItems.includes(value)
              ? setShowUpgradeDialog(true)
              : toggleItemStatus(
                  value,
                  action,
                  isSubOptions,
                  isAllSubOptionsChanged
                )
          }
          disableItems={disableItems}
          preview={preview}
          isSupportSubOptions={isSupportSubOptions}
          type={'disable'}
        />
      </div>
      <ClickDisabledPopup
        showUpgradeDialog={showUpgradeDialog}
        setShowUpgradeDialog={setShowUpgradeDialog}
        isEnterprise={isEnterprise}
        type={type}
      />
    </>
  );
}
